import React, { useState, useEffect, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { ref, onValue, off, query, orderByChild, limitToLast } from 'firebase/database';
import { useFirebase } from '../FirebaseContext';
import './Chat.css';

import ChatList from '../components/chat/ChatList';
import ChatWindow from '../components/chat/ChatWindow';

const Chat = () => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState({});
  
  // Получаем текущий экземпляр Firebase из контекста
  const { currentFirebase, currentConfig } = useFirebase();
  const { auth, database, firestore } = currentFirebase;

  // Сбрасываем состояние при смене конфигурации
  useEffect(() => {
    console.log('Chat: Firebase config changed to:', currentConfig);
    setSelectedChat(null);
    setChats([]);
    setUser(null);
    setUserInfo({});
  }, [currentConfig]);

  // Следим за состоянием аутентификации
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      console.log('Chat: Auth state changed:', currentUser?.uid);
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(firestore, 'users', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            setUserInfo(userDocSnap.data());
            console.log('Chat: User info loaded');
          }
        } catch (error) {
          console.error('Chat: Error fetching user info:', error);
          setUserInfo({});
        }
      } else {
        setUser(null);
        setUserInfo({});
      }
    });
    
    return () => unsubscribe();
  }, [auth, firestore, currentConfig]);
  
  // Загружаем чаты
  useEffect(() => {
    if (!user || !database) {
      console.log('Chat: No user or database, skipping chat load');
      return;
    }

    console.log('Chat: Setting up chats listener for config:', currentConfig);
    const chatsRef = ref(database, 'chats');
    
    const unsubscribe = onValue(
      chatsRef, 
      (snapshot) => {
        const loadedChats = [];
        if (snapshot.exists()) {
          snapshot.forEach((snap) => {
            const chat = { id: snap.key, ...snap.val() };
            let unreadCount = 0;
            if (chat.messages) {
              Object.values(chat.messages).forEach((msg) => {
                if (!msg.isRead && msg.senderRole === 'user') unreadCount++;
              });
            }
            loadedChats.push({ ...chat, unreadCount });
          });
        }
        
        // НЕ сортируем здесь - сортировка происходит в ChatList для лучшей производительности
        console.log('Chat: Loaded', loadedChats.length, 'chats');
        setChats(loadedChats);

        // Не выбираем чат автоматически — пользователь сам выберет из списка
      },
      (error) => {
        console.error('Chat: Error loading chats:', error);
        setChats([]);
      }
    );
    
    return () => {
      console.log('Chat: Cleaning up chats listener');
      off(chatsRef);
    };
  }, [database, user, currentConfig]);
  
  // Обновляем selectedChat только если он был удален (не обновляем данные!)
  useEffect(() => {
    if (!selectedChat || chats.length === 0) return;

    // Проверяем только существование чата, НЕ обновляем его данные
    const chatStillExists = chats.some(c => c.id === selectedChat.id);

    // Обновляем selectedChat только если он был УДАЛЕН
    if (!chatStillExists && chats.length > 0) {
      setSelectedChat(chats[0]);
    }
  }, [chats, selectedChat]);

  // Мемоизируем актуальные данные выбранного чата
  // Это позволяет получать свежие данные без переключения selectedChat
  const currentChatData = useMemo(() => {
    if (!selectedChat) return null;
    return chats.find(c => c.id === selectedChat.id) || selectedChat;
  }, [chats, selectedChat]);

  const handleSelectChat = (chatId) => {
    const chat = chats.find((c) => c.id === chatId);
    console.log('Chat: handleSelectChat, chatId:', chatId, 'Found:', !!chat);
    if (chat) {
      setSelectedChat(chat);
    }
  };
  
  // Стили для layout
  const containerStyle = {
    display: 'flex',
    height: '100%',
    margin: 0,
    padding: 0,
    overflow: 'hidden',
  };

  const leftColStyle = {
    width: '30%',
    overflowY: 'auto',
    borderRight: '1px solid #e8e8e8',
  };

  const rightColStyle = {
    width: '70%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  // Стиль для отображения текущей конфигурации
  const configBadgeStyle = {
    position: 'fixed',
    top: 10,
    right: 10,
    backgroundColor: '#1890ff',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    zIndex: 1000,
  };

  return (
    <div style={containerStyle}>
      {/* Бейдж с текущей конфигурацией */}


      {/* Левая колонка — список чатов */}
      <div style={leftColStyle} className="hide-scrollbar">
        <ChatList
          chats={chats}
          onSelectChat={handleSelectChat}
          selectedChatId={selectedChat?.id}
        />
      </div>

      {/* Правая колонка — окно выбранного чата */}
      <div style={rightColStyle}>
        {currentChatData && user ? (
          <ChatWindow
            key={`${selectedChat.id}-${currentConfig}`} // Важно для пересоздания компонента
            selectedChat={currentChatData}
            user={user}
            userInfo={userInfo}
          />
        ) : (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            flexDirection: 'column',
            gap: '10px'
          }}>
            <h2>{user ? 'Wybierz czat z listy' : 'Zaloguj się, aby zobaczyć czaty'}</h2>
            {user && chats.length === 0 && (
              <p style={{ color: '#999' }}>Brak dostępnych czatów</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;